/**
 * Events Manager Page
 * CRUD interface for managing calendar events with draft/publish workflow
 */
import { useState } from 'react'
import { useEvents, useCreateEvent, useUpdateEvent, useDeleteEvent } from '../hooks/useEvents'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Spinner } from '../components/ui/Spinner'
import { Alert } from '../components/ui/Alert'
import { EventForm } from '../components/EventForm'
import { Calendar, Plus, Edit2, Trash2 } from 'lucide-react'
import type { Event, EventInput } from '../types'

export function EventsPage() {
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)

  const { data, isLoading, error } = useEvents()
  const createMutation = useCreateEvent()
  const updateMutation = useUpdateEvent()
  const deleteMutation = useDeleteEvent()

  const handleCreate = async (formData: EventInput) => {
    await createMutation.mutateAsync(formData)
    setIsAddModalOpen(false)
  }

  const handleUpdate = async (formData: EventInput) => {
    if (!editingEvent) return
    await updateMutation.mutateAsync({ id: editingEvent.id, data: formData })
    setEditingEvent(null)
  }

  // Convert ISO 8601 to datetime-local format for editing
  const toDatetimeLocal = (isoString: string | undefined): string => {
    if (!isoString) return ''
    // datetime-local format: YYYY-MM-DDTHH:mm (no seconds, no timezone)
    return isoString.slice(0, 16)
  }

  // Prepare event for editing with datetime-local formatted dates
  const prepareEventForEdit = (event: Event): Event => ({
    ...event,
    date: toDatetimeLocal(event.date),
    end_date: event.end_date ? toDatetimeLocal(event.end_date) : undefined,
  })

  const handleDelete = async (event: Event) => {
    if (!confirm(`Delete "${event.title}"? This action cannot be undone.`)) return
    await deleteMutation.mutateAsync(event.id)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  // Strip HTML tags for plain text display
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const getStatus = (event: Event) => {
    return event.published === 1 ? 'published' : 'draft'
  }

  const getCategoryBadge = (category?: string) => {
    if (!category) return null

    const colors = {
      game: 'bg-blue-100 text-blue-800',
      practice: 'bg-gray-100 text-gray-800',
      competition: 'bg-purple-100 text-purple-800',
      fundraiser: 'bg-green-100 text-green-800',
      community: 'bg-yellow-100 text-yellow-800',
    }

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${colors[category as keyof typeof colors]}`}
      >
        {category}
      </span>
    )
  }

  if (isLoading) return <Spinner />
  if (error) return <Alert variant="error">{(error as Error).message}</Alert>

  const events = data?.events || []

  // Filter events
  const filteredEvents = events.filter((event) => {
    const status = getStatus(event)
    if (filter === 'published') return status === 'published'
    if (filter === 'draft') return status === 'draft'
    return true
  })

  return (
    <>
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-coolgray shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading text-rhs-maroon truncate flex items-center gap-2">
                <Calendar size={28} />
                Events Manager
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {events.length} total events
              </p>
            </div>

            <Button variant="primary" onClick={() => setIsAddModalOpen(true)} className="shrink-0">
              <Plus size={16} />
              New Event
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {(['all', 'published', 'draft'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  filter === tab
                    ? 'bg-rhs-maroon text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Event List */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {filteredEvents.length === 0 ? (
          <Alert variant="info">No events found.</Alert>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event) => {
              const status = getStatus(event)
              return (
                <div
                  key={event.id}
                  className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {event.title}
                      </h3>
                      {event.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {stripHtml(event.description)}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        <span
                          className={`px-2 py-1 rounded-full font-medium ${
                            status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {status}
                        </span>
                        {getCategoryBadge(event.category)}
                        <span>📅 {formatDate(event.date)}</span>
                        {event.location && <span>📍 {event.location}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setEditingEvent(event)}>
                        <Edit2 size={14} />
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(event)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add Event Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="New Event">
        <EventForm
          onSubmit={handleCreate}
          onCancel={() => setIsAddModalOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      {/* Edit Event Modal */}
      <Modal isOpen={!!editingEvent} onClose={() => setEditingEvent(null)} title="Edit Event">
        {editingEvent && (
          <EventForm
            event={prepareEventForEdit(editingEvent)}
            onSubmit={handleUpdate}
            onCancel={() => setEditingEvent(null)}
            isSubmitting={updateMutation.isPending}
          />
        )}
      </Modal>
    </>
  )
}
